import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { ProvinceYearStatsSchema } from '../../../services/provinceApi';
import { z } from 'zod';
import { useAppSelector } from '../../../store/hooks';
import { MOBILE_CELL_STYLES, MOBILE_GUIDE } from '../../../utils/responsive';

interface UseProvincesColumnsProps {
	isMobile?: boolean;
	provinceStats?: { stats: z.infer<typeof ProvinceYearStatsSchema>[] };
}

/**
 * Cột cho lưới tỉnh SAU SÁP NHẬP. Hàng ở đây là kết quả tổng hợp
 * (`maTinh` là mã sau sáp nhập), không phải một hàng của bảng `tinh` gốc, nên
 * KHÔNG có cột Sửa/Xoá — endpoint `PUT/DELETE /provinces/:ref` tra theo mã
 * tỉnh gốc và sẽ sửa/xoá nhầm tỉnh nếu nhận mã sau sáp nhập.
 */
export function useProvincesColumns({ isMobile = false, provinceStats }: UseProvincesColumnsProps) {
	const years = useAppSelector((state) => state.filters.years);


	return useMemo<GridColDef[]>(
		() => {
			// Mobile: condensed single column with code, name, and latest year
			if (isMobile) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const latestYear = sortedYears[sortedYears.length - 1];
				const provinceStatsMap = new Map<string, Map<string, number>>();

				if (provinceStats?.stats) {
					provinceStats.stats.forEach((provinceStat) => {
						if (!provinceStatsMap.has(provinceStat.maTinh)) {
							provinceStatsMap.set(provinceStat.maTinh, new Map());
						}
						const yearDataMap = provinceStatsMap.get(provinceStat.maTinh)!;
						Object.entries(provinceStat.yearData).forEach(([year, count]) => {
							yearDataMap.set(year, count as number);
						});
					});
				}

				return [
					{
						field: 'tenTinh',
						headerName: 'Tỉnh/Thành phố',
						minWidth: MOBILE_CELL_STYLES.MIN_WIDTH,
						flex: 1,
						sortable: true,
						filterable: false,
						renderCell: ({ row }) => {
							const yearDataMap = provinceStatsMap.get(row.maTinh);
							const latestCount = latestYear && yearDataMap ? yearDataMap.get(latestYear.toString()) || 0 : 0;
							return (
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'center',
										padding: MOBILE_CELL_STYLES.PADDING,
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										lineHeight: MOBILE_CELL_STYLES.LINE_HEIGHT,
										gap: MOBILE_CELL_STYLES.GAP,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										{row.maTinh || 'N/A'}
									</Typography>
									<Typography variant="body2" fontWeight="bold">
										{row.tenTinh || 'N/A'}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{row.soTruong || 0} trường
									</Typography>
									{latestYear && (
										<Typography variant="caption" color="text.secondary">
											{latestYear}: {latestCount} sinh viên
										</Typography>
									)}
									<Typography variant="caption" color={MOBILE_GUIDE.COLOR} fontWeight={MOBILE_GUIDE.FONT_WEIGHT}>
										{MOBILE_GUIDE.TEXT}
									</Typography>
								</Box>
							);
						},
					},
				];
			}

			const baseColumns: GridColDef[] = [
				{
					field: 'stt',
					headerName: 'STT',
					width: 60,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ api, id }) => {
						const currentPage = api.state.pagination.paginationModel.page;
						const pageSize = api.state.pagination.paginationModel.pageSize;
						const allRows = api.getRowModels();
						const rowIndex = Array.from(allRows.keys()).indexOf(id as string);
						const stt = currentPage * pageSize + rowIndex + 1;
						return stt;
					},
				},
				{
					field: 'maTinh',
					headerName: 'Mã Tỉnh',
					width: 150,
					headerAlign: 'center',
					align: 'center',
					sortable: true,
					filterable: false
				},
				{
					field: 'tenTinh',
					headerName: 'Tên Tỉnh',
					width: 300,
					headerAlign: 'center',
					sortable: true,
					filterable: false,
				},
				{
					field: 'soTruong',
					headerName: 'Số Trường',
					width: 100,
					type: 'number',
					headerAlign: 'center',
					align: 'center',
					filterable: false,
					sortable: true,
				},
			];

			const yearColumns: GridColDef[] = [];
			if (years.length > 0 && provinceStats?.stats) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const provinceStatsMap = new Map<string, Map<string, number>>();

				provinceStats.stats.forEach((provinceStat) => {
					if (!provinceStatsMap.has(provinceStat.maTinh)) {
						provinceStatsMap.set(provinceStat.maTinh, new Map());
					}
					const yearDataMap = provinceStatsMap.get(provinceStat.maTinh)!;
					Object.entries(provinceStat.yearData).forEach(([year, count]) => {
						yearDataMap.set(year, count as number);
					});
				});

				sortedYears.slice(-5).forEach((year: number) => {
					yearColumns.push({
						field: year.toString(),
						headerName: year.toString(),
						width: 80,
						type: 'number',
						headerAlign: 'center',
						sortable: true,
						filterable: false,
						align: 'center',
						renderCell: ({ row }: { row: { maTinh: string } }) => {
							const yearDataMap = provinceStatsMap.get(row.maTinh);
							const count = yearDataMap ? yearDataMap.get(year.toString()) || 0 : 0;
							return count;
						},
					});
				});
			}

			return [...baseColumns, ...yearColumns];
		},
		[years, provinceStats, isMobile],
	);
}